import { IntegrationEnhanced } from "@/api/integrations";
import { ConnectionContent } from "@/components/ConnectionContent";
import IntegrationSection from "@/components/IntegrationSection";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useSettingsInDialog } from "@/context/SettingsInDialogContext";
import { useSettingsInitialNavigation } from "@/context/SettingsInitialNavigationContext";
import { useConnectionsSearch } from "@/hooks/useConnectionsSearch";
import { parseConnectorsIntegrationIdFromPath } from "@/utils/settingsNavigation";
import {
  IndexLayoutContent,
  IndexLayoutStickyHeader,
  ZeroStateMotion,
} from "@cohesive/ui-components";
import { CheckCircle, ChevronLeft, Compass, Plug, Search } from "lucide-react";
import React, { useEffect, useState } from "react";
import { MyConnectionsSkeleton } from "../MyConnections.skeleton";
import { MyConnectionsHeader } from "./MyConnectionsHeader";

interface MyConnectionsProps {
  context?: "page" | "pane";
}

const MyConnections: React.FC<MyConnectionsProps> = ({ context = "page" }) => {
  const isInDialog = useSettingsInDialog();
  const { pathname: initialPathname } = useSettingsInitialNavigation();
  const initialConnectorsIntegrationId =
    parseConnectorsIntegrationIdFromPath(initialPathname);
  const [selectedIntegrationId, setSelectedIntegrationId] = useState<
    string | null
  >(null);

  useEffect(() => {
    if (isInDialog && initialConnectorsIntegrationId) {
      setSelectedIntegrationId(initialConnectorsIntegrationId);
      return;
    }
    if (isInDialog && !initialConnectorsIntegrationId) {
      setSelectedIntegrationId(null);
    }
  }, [isInDialog, initialConnectorsIntegrationId]);

  const {
    // Filter states
    searchTerm,
    setSearchTerm,
    selectedCategory,
    setSelectedCategory,
    showConnected,
    setShowConnected,

    discoverableIntegrations,
    connectedIntegrations,
    availableIntegrations,
    allCategories,
    connectedIntegrationIds,

    // Handlers
    handleIntegrationConnect,
    handleIntegrationClick: _handleIntegrationClick,
    handleIntegrationManage: _handleIntegrationManage,
    handleClearFilters,

    // Action type logic
    getActionType,

    // States
    isLoading,
    showZeroState,

    // Utils
    getCategoryDisplayName,
  } = useConnectionsSearch({ context });

  const handleIntegrationClick = isInDialog
    ? (integration: IntegrationEnhanced) =>
        setSelectedIntegrationId(integration.id)
    : _handleIntegrationClick;

  const handleIntegrationManage = isInDialog
    ? (integrationId: string) => setSelectedIntegrationId(integrationId)
    : _handleIntegrationManage;

  if (isLoading) {
    return <MyConnectionsSkeleton />;
  }

  if (isInDialog && selectedIntegrationId) {
    return (
      <div className="flex flex-col min-h-0">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="self-start -ml-2 mb-4 text-muted-foreground hover:text-foreground gap-1.5"
          onClick={() => setSelectedIntegrationId(null)}
        >
          <ChevronLeft className="h-4 w-4" />
          Back to Connectors
        </Button>
        <ConnectionContent
          integrationId={selectedIntegrationId}
          context="pane"
          showHeader={true}
        />
      </div>
    );
  }

  return (
    <>
      <title>My Connectors</title>
      <div className="relative pb-8 w-full flex flex-col flex-1">
        <IndexLayoutStickyHeader>
          <MyConnectionsHeader
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            showConnected={showConnected}
            setShowConnected={setShowConnected}
            allCategories={allCategories}
            handleClearFilters={handleClearFilters}
            getCategoryDisplayName={getCategoryDisplayName}
          />
        </IndexLayoutStickyHeader>
        {showZeroState ? (
          <IndexLayoutContent className="mt-4">
            <ZeroStateMotion>
              <Card className="relative bg-background/50 backdrop-blur-sm p-12 text-center max-w-md mx-auto flex flex-col items-center justify-center min-h-[400px]">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary/10 to-plum/10 flex items-center justify-center mb-6 mx-auto">
                  <Search className="h-10 w-10 text-primary" />
                </div>
                <h3 className="text-2xl font-semibold mb-2">
                  No integrations found
                </h3>
                <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                  {searchTerm || selectedCategory !== "all"
                    ? "Try adjusting your search criteria or filters to find what you're looking for."
                    : "No integrations are available at the moment."}
                </p>
                {(searchTerm ||
                  selectedCategory !== "all" ||
                  !showConnected) && (
                  <Button variant="outline" onClick={handleClearFilters}>
                    Clear all filters
                  </Button>
                )}
              </Card>
            </ZeroStateMotion>
          </IndexLayoutContent>
        ) : (
          <IndexLayoutContent className="mt-4">
            <div className="space-y-12">
              {showConnected && connectedIntegrations.length > 0 && (
                <IntegrationSection
                  title="Connected"
                  integrations={connectedIntegrations}
                  icon={CheckCircle}
                  iconColorClasses="bg-gradient-to-br from-lc-active/10 to-spectrum-green/10"
                  connectedIntegrationIds={connectedIntegrationIds}
                  handleIntegrationClick={handleIntegrationClick}
                  handleIntegrationConnect={handleIntegrationConnect}
                  handleIntegrationManage={handleIntegrationManage}
                  getActionType={getActionType}
                  isConnectedSection={true}
                />
              )}
              {availableIntegrations.length > 0 && (
                <IntegrationSection
                  title="Available"
                  integrations={availableIntegrations}
                  icon={Plug}
                  iconColorClasses="bg-gradient-to-br from-rust/10 to-rust/10"
                  connectedIntegrationIds={connectedIntegrationIds}
                  handleIntegrationClick={handleIntegrationClick}
                  handleIntegrationConnect={handleIntegrationConnect}
                  handleIntegrationManage={handleIntegrationManage}
                  getActionType={getActionType}
                />
              )}
              {discoverableIntegrations.length > 0 && (
                <IntegrationSection
                  title="Request Access"
                  integrations={discoverableIntegrations}
                  icon={Compass}
                  iconColorClasses="bg-gradient-to-br from-plum/10 to-purple-600/10"
                  connectedIntegrationIds={connectedIntegrationIds}
                  handleIntegrationClick={handleIntegrationClick}
                  handleIntegrationConnect={handleIntegrationConnect}
                  handleIntegrationManage={handleIntegrationManage}
                  getActionType={getActionType}
                />
              )}
            </div>
          </IndexLayoutContent>
        )}
      </div>
    </>
  );
};

export default MyConnections;
